import React, { useState, useEffect } from 'react';

interface Rift {
  id: string;
  name: string;
  layer: number;
  width: number;
  height: number;
  totalChunks: number;
  seed: number;
  theme: string | null;
  anomalyLevel: number;
  discoveredAt: string | null;
  discoveredBy: string | null;
  isPublic: boolean;
  minLevel: number | null;
  isActive: boolean;
  createdAt: string;
}

interface RiftScar {
  id: string;
  name: string;
  sourceRift: string;
  destinationRift: string;
  pairedScarId: string | null;
  worldX: number;
  worldY: number;
  radius: number;
  stability: 'stable' | 'sealed';
  isDiscovered: boolean;
  discoveredBy: string | null;
  dangerLevel: number;
  effectType: string;
  isActive: boolean;
}

interface RiftManagementPanelProps {
  apiBaseUrl: string;
  adminKey: string;
}


const StatCard: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <div style={{ padding: '12px 16px', backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', minWidth: 100 }}>
    <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 16, fontWeight: 600, color: color || '#e2e8f0' }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
  </div>
);

export const RiftManagementPanel: React.FC<RiftManagementPanelProps> = ({ apiBaseUrl, adminKey }) => {
  const [rifts, setRifts] = useState<Rift[]>([]);
  const [scars, setScars] = useState<RiftScar[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [selectedRift, setSelectedRift] = useState<Rift | null>(null);

  const fetchRifts = async (): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/rifts`, {
        headers: { 'x-admin-key': adminKey }
      });
      if (response.ok) {
        const data: Rift[] = await response.json();
        setRifts(data);
      }
    } catch (error) {
      console.error('Failed to fetch rifts:', error);
    }
  };

  const fetchScars = async (): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/rift-scars`, {
        headers: { 'x-admin-key': adminKey }
      });
      if (response.ok) {
        const data: RiftScar[] = await response.json();
        setScars(data);
      }
    } catch (error) {
      console.error('Failed to fetch rift scars:', error);
    }
  };

  const loadData = async (): Promise<void> => {
    setLoading(true);
    await Promise.all([fetchRifts(), fetchScars()]);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, adminKey]);

  const toggleRiftActive = async (riftId: string, isActive: boolean): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/rifts/${riftId}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ isActive })
      });

      if (response.ok) {
        setMessage({ text: `Rift ${isActive ? 'enabled' : 'disabled'} successfully`, type: 'success' });
        await fetchRifts();
      } else {
        setMessage({ text: 'Failed to toggle rift', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error toggling rift', type: 'error' });
    }
  };

  const toggleScarActive = async (scarId: string, isActive: boolean): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/rift-scars/${scarId}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ isActive })
      });

      if (response.ok) {
        setMessage({ text: `Rift scar ${isActive ? 'enabled' : 'disabled'} successfully`, type: 'success' });
        await fetchScars();
      } else {
        setMessage({ text: 'Failed to toggle rift scar', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error toggling rift scar', type: 'error' });
    }
  };

  const setScarStability = async (scarId: string, stability: 'stable' | 'sealed'): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/rift-scars/${scarId}/stability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ stability })
      });

      if (response.ok) {
        setMessage({ text: `Rift scar ${stability === 'sealed' ? 'sealed' : 'opened'} successfully`, type: 'success' });
        await fetchScars();
      } else {
        setMessage({ text: 'Failed to set rift scar stability', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error setting rift scar stability', type: 'error' });
    }
  };


  if (loading) {
    return <div style={{ color: '#e2e8f0', fontFamily: '"Sometype Mono", monospace' }}>Loading rift data...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Message Toast */}
      {message && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 20px',
          backgroundColor: message.type === 'success' ? '#22c55e' : '#ef4444',
          color: '#fff',
          borderRadius: 4,
          zIndex: 1000,
          fontFamily: '"Sometype Mono", monospace',
          fontSize: 12,
        }}>
          {message.text}
        </div>
      )}

      {/* Header with Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ margin: 0, color: '#ffa500', fontFamily: '"Sometype Mono", monospace', fontSize: 14 }}>
            🌊 RIFT MANAGEMENT
          </h3>
          <span style={{
            padding: '4px 8px',
            backgroundColor: rifts.some(r => r.isActive) ? '#22c55e22' : '#ef444422',
            color: rifts.some(r => r.isActive) ? '#22c55e' : '#ef4444',
            fontSize: 10,
            borderRadius: 4,
            fontWeight: 600,
          }}>
            {rifts.filter(r => r.isActive).length} ACTIVE
          </span>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          style={{
            padding: '6px 12px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            border: 'none',
            fontSize: 11,
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          Refresh Data
        </button>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Total Rifts" value={rifts.length} />
        <StatCard label="Active Rifts" value={rifts.filter(r => r.isActive).length} color="#22c55e" />
        <StatCard label="Total Scars" value={scars.length} color="#a855f7" />
        <StatCard label="Active Scars" value={scars.filter(s => s.isActive).length} color="#10b981" />
        <StatCard label="Sealed Scars" value={scars.filter(s => s.stability === 'sealed').length} color="#ef4444" />
        <StatCard label="Discovered" value={scars.filter(s => s.isDiscovered).length} color="#f59e0b" />
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Rift List */}
        <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
          <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
            Rifts ({rifts.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
            {rifts.map((rift) => (
              <div
                key={rift.id}
                onClick={() => setSelectedRift(rift)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: selectedRift?.id === rift.id ? '#1a1a2e' : '#0a0a12',
                  border: selectedRift?.id === rift.id ? '1px solid #3b82f6' : '1px solid transparent',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: rift.isActive ? '#22c55e' : '#ef4444',
                  }} />
                  <span style={{ color: '#e2e8f0', fontSize: 12 }}>{rift.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#f59e0b', fontSize: 10 }}>L{rift.layer}</span>
                  <span style={{ color: '#718096', fontSize: 10 }}>{rift.width}×{rift.height}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rift Details / Scars */}
        <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
          {selectedRift ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', textTransform: 'uppercase' }}>
                  {selectedRift.name}
                </div>
                <button
                  type="button"
                  onClick={() => toggleRiftActive(selectedRift.id, !selectedRift.isActive)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: selectedRift.isActive ? '#ef4444' : '#22c55e',
                    color: '#fff',
                    border: 'none',
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  {selectedRift.isActive ? 'Disable' : 'Enable'}
                </button>
              </div>

              {/* Rift Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                <div style={{ padding: 8, backgroundColor: '#1a1a2e', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#718096' }}>Layer</div>
                  <div style={{ fontSize: 14, color: '#f59e0b', fontWeight: 600 }}>{selectedRift.layer}</div>
                </div>
                <div style={{ padding: 8, backgroundColor: '#1a1a2e', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#718096' }}>Size</div>
                  <div style={{ fontSize: 14, color: '#3b82f6', fontWeight: 600 }}>{selectedRift.width}×{selectedRift.height}</div>
                </div>
                <div style={{ padding: 8, backgroundColor: '#1a1a2e', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#718096' }}>Anomaly</div>
                  <div style={{ fontSize: 14, color: selectedRift.anomalyLevel > 5 ? '#f59e0b' : '#22c55e', fontWeight: 600 }}>{selectedRift.anomalyLevel}/10</div>
                </div>
              </div>

              {/* Rift Info */}
              <div style={{ marginBottom: 16, fontSize: 10, color: '#718096' }}>
                {selectedRift.theme && <div>Theme: <span style={{ color: '#e2e8f0' }}>{selectedRift.theme}</span></div>}
                {selectedRift.minLevel && <div>Min Level: <span style={{ color: '#e2e8f0' }}>{selectedRift.minLevel}</span></div>}
                {selectedRift.discoveredBy && <div>Discovered: <span style={{ color: '#22c55e' }}>{selectedRift.discoveredBy}</span></div>}
              </div>

              {/* Scars */}
              <div>
                <div style={{ fontSize: 10, color: '#718096', marginBottom: 8, fontWeight: 600 }}>SCARS ({scars.filter(s => s.sourceRift === selectedRift.id).length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {scars.filter(s => s.sourceRift === selectedRift.id).length > 0 ? (
                    scars.filter(s => s.sourceRift === selectedRift.id).map((scar) => (
                      <div key={scar.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', backgroundColor: '#1a1a2e', borderRadius: 4, borderLeft: `3px solid ${scar.dangerLevel > 5 ? '#f59e0b' : '#3b82f6'}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: '#e2e8f0', fontWeight: 600 }}>{scar.name}</div>
                          <div style={{ fontSize: 9, color: '#718096' }}>Danger: {scar.dangerLevel}/10</div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            type="button"
                            onClick={() => setScarStability(scar.id, scar.stability === 'stable' ? 'sealed' : 'stable')}
                            style={{
                              padding: '3px 6px',
                              backgroundColor: scar.stability === 'stable' ? '#3b82f6' : '#ef4444',
                              color: '#fff',
                              border: 'none',
                              fontSize: 9,
                              cursor: 'pointer',
                            }}
                          >
                            {scar.stability === 'stable' ? 'OPEN' : 'SEALED'}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleScarActive(scar.id, !scar.isActive)}
                            style={{
                              padding: '3px 6px',
                              backgroundColor: scar.isActive ? '#22c55e' : '#718096',
                              color: '#fff',
                              border: 'none',
                              fontSize: 9,
                              cursor: 'pointer',
                            }}
                          >
                            {scar.isActive ? 'ON' : 'OFF'}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#718096', fontSize: 10, textAlign: 'center', padding: 12 }}>No scars</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ color: '#718096', fontSize: 11, textAlign: 'center', padding: 20 }}>Select a rift to view details</div>
          )}
        </div>
      </div>

      {/* Warning Box */}
      <div style={{
        backgroundColor: '#1a1a2e',
        border: '1px solid #2a2a3e',
        padding: 12,
        borderRadius: 4,
      }}>
        <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>⚠️ WARNING</div>
        <div style={{ color: '#a0aec0', fontSize: 10, lineHeight: '1.5' }}>
          Disabling rifts or sealing scars will prevent players from accessing those areas. Communicate changes to players before making modifications.
        </div>
      </div>
    </div>
  );
};
